import React from 'react';
import { StyleSheet, View, Text } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

/**
 * A card component for displaying statistics with an icon
 * 
 * @param {object} props 
 * @param {string} props.title - Title of the stat card
 * @param {string} props.value - Value to display in the stat card (e.g., "₹12,345")
 * @param {string} props.subtitle - Optional subtitle/description text
 * @param {string} props.iconName - Name of the Ionicons icon to display
 * @param {string} props.iconColor - Color of the icon (default: #3498db)
 * @param {string} props.trend - Optional trend indicator ('up', 'down', or null)
 * @param {string} props.trendValue - Optional trend value (e.g., "5%")
 * @param {object} props.style - Additional style for the card container
 */
const StatCard = ({
  title,
  value,
  subtitle,
  iconName,
  iconColor = '#3498db',
  trend,
  trendValue,
  style,
}) => {
  const renderTrend = () => {
    if (!trend) return null;

    const isUp = trend === 'up';
    
    return (
      <View style={[styles.trendContainer, isUp ? styles.trendUp : styles.trendDown]}>
        <Ionicons
          name={isUp ? 'arrow-up' : 'arrow-down'}
          size={12}
          color={isUp ? '#2ecc71' : '#e74c3c'}
        />
        <Text style={[styles.trendText, isUp ? styles.trendUpText : styles.trendDownText]}>
          {trendValue}
        </Text>
      </View>
    );
  };

  return (
    <View style={[styles.container, style]}>
      <View style={styles.header}>
        <Text style={styles.title}>{title}</Text>
        <View style={[styles.iconContainer, { backgroundColor: `${iconColor}20` }]}>
          <Ionicons name={iconName} size={20} color={iconColor} />
        </View>
      </View>
      
      <View style={styles.valueContainer}>
        <Text style={styles.value}>{value}</Text>
        {renderTrend()}
      </View>
      
      {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  title: {
    fontSize: 14,
    color: '#7f8c8d',
    fontWeight: '500',
  },
  iconContainer: {
    width: 32,
    height: 32,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  valueContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: 4,
  },
  value: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginRight: 8,
  },
  subtitle: {
    fontSize: 12,
    color: '#95a5a6',
  },
  trendContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  trendUp: {
    backgroundColor: '#2ecc7120',
  },
  trendDown: {
    backgroundColor: '#e74c3c20',
  },
  trendText: {
    fontSize: 12,
    fontWeight: '500',
    marginLeft: 2,
  },
  trendUpText: {
    color: '#2ecc71',
  },
  trendDownText: {
    color: '#e74c3c',
  },
});

export default StatCard;